/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { EventAggregator } from 'aurelia-event-aggregator';
import { AuthStatusReasonReloadEvent } from 'app/um/lib/events';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { forceNull } from 'app/shared/lib/utilities';
import { AuthService } from 'app/um/services/auth-service';
import { I18N } from 'aurelia-i18n';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class SaveReason extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reload' })
    public reason: UM.AuthStatusReason = {};
    protected reasonId: string | null = null;

    protected selectedStatus: UM.AuthStatusReason | null = null;
    protected activeStatuses: ReadonlyArray<UM.AuthStatus> | null = null;

    protected statusReason: string = '';
    protected reasonCode: string = '';

    protected originalReason: UM.AuthStatusReason = {};

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    private async reload(): Promise<void> {
        await this.loadLookups();
        if(!this.reason) return;

        // Params for reason info
        this.reasonId = this.reason.id || null;
        this.selectedStatus = this.reason.status || null;
        this.statusReason = this.reason.description || '';
        this.reasonCode = this.reason.code || '';
        this.originalReason = this.buildReason();
    }

    protected buildReason(): UM.AuthStatusReason {
        const saveReason: UM.AuthStatusReason = {
            id: this.reason.id,
            description: this.statusReason,
            code: forceNull(this.reasonCode),
            status: { id: this.selectedStatus?.id },
        };

        return saveReason;
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        const isSuccess = await this.saveReason();
        this.events.publish(new AuthStatusReasonReloadEvent());
        return isSuccess;
    }

    public reset(): void {
        this.selectedStatus = this.originalReason.status || {};
        this.statusReason = this.originalReason.description || '';
        this.reasonCode = this.originalReason.code || '';
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: this.reason && !!this.reason?.id
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadAuthStatuses(options);
            this.activeStatuses = response.results;

        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveReason(): Promise<boolean> {
        const insertReason: UM.AuthStatusReason = this.buildReason();

        let isSuccessful = false;
        let invalidCodeError = false;
        try {
            this.state.beginSave();
            const response = await this.service.saveAuthStatusReason(insertReason);
            isSuccessful = response.isSuccessful;

            return isSuccessful;
        }
        catch(error) {
            if(error.status.errors) {
                invalidCodeError = error.status.errors.some((err: any) => err.code === 'property.constraintViolated' && (err.description === 'Duplicate AuthStatusReasonCode value exists' || err.property === 'code'));
            }
            throw error;
        }
        finally {
            if(invalidCodeError) {
                this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.save_error', { what: this.i18n.tr('um:auth.status.reason.code.$long'), item: this.i18n.tr('um:auth.status.reason.$long') })).show();
            } else {
                this.toaster.standard.saved('um:auth.status.reason.$long', isSuccessful);
            }

            this.state.endSave();
        }
    }
}


